import httpClient, { handleAPIError, createSuccessResponse } from './httpClient.js';

/**
 * FilterAPI - Handles all filter-related API operations
 *
 * Consolidates filter endpoints and provides unified interface
 * for fetching filter options (countries, commodities, mine types, statuses)
 */
export class FilterAPI {
  constructor() {
    this.client = httpClient;
  }

  /**
   * Fetch all available filter options
   * @returns {Promise<Object>} Response with all filter options
   */
  async fetchFilterOptions() {
    try {
      const response = await this.client.get('/filters/options');
      return createSuccessResponse(response.data, response.status);
    } catch (error) {
      return handleAPIError(error, { operation: 'fetchFilterOptions' });
    }
  }

  /**
   * Fetch countries list (raw format)
   * @returns {Promise<Object>} Response with countries data
   */
  async fetchCountries() {
    try {
      const response = await this.client.get('/filters/countries');
      return createSuccessResponse(response.data, response.status);
    } catch (error) {
      return handleAPIError(error, { operation: 'fetchCountries' });
    }
  }

  /**
   * Fetch commodities list (raw format)
   * @returns {Promise<Object>} Response with commodities data
   */
  async fetchCommodities() {
    try {
      const response = await this.client.get('/filters/commodities');
      return createSuccessResponse(response.data, response.status);
    } catch (error) {
      return handleAPIError(error, { operation: 'fetchCommodities' });
    }
  }

  /**
   * Unified method to fetch filter options by type
   * Replaces the 4 separate getXxxFilterOptions methods
   *
   * @param {string} filterType - Type of filter ('countries', 'commodities', 'mine-types', 'mine-status')
   * @returns {Promise<Object>} Response with formatted filter options
   */
  async getFilterOptions(filterType) {
    const endpointMap = {
      'countries': '/filters/countries',
      'commodities': '/filters/commodities',
      'mine-types': '/filters/mine-types',
      'mine-status': '/filters/mine-status'
    };

    const endpoint = endpointMap[filterType];
    if (!endpoint) {
      return handleAPIError(new Error(`Invalid filter type: ${filterType}`), {
        operation: 'getFilterOptions',
        filterType
      });
    }

    try {
      const response = await this.client.get(endpoint);
      const options = this._formatFilterOptions(filterType, response.data);

      return {
        success: true,
        options: options,
        status: response.status
      };
    } catch (error) {
      return handleAPIError(error, {
        operation: 'getFilterOptions',
        filterType
      });
    }
  }

  /**
   * Format filter options based on type
   * Handles the different data structures returned by each endpoint
   *
   * @private
   * @param {string} filterType - Type of filter
   * @param {Object} data - Raw response data
   * @returns {Array} Formatted options array
   */
  _formatFilterOptions(filterType, data) {
    switch (filterType) {
      case 'countries':
        const countries = data.countries || [];
        return countries.map(country => ({
          value: country.country_name,
          label: country.country_name,
          count: country.mine_count
        }));

      case 'commodities':
        const commodities = data.commodities || [];
        return commodities.map(commodity => ({
          value: commodity.commodity_name,
          label: commodity.commodity_name,
          count: commodity.mine_count
        }));

      case 'mine-types':
        const mineTypes = data.options || [];
        return mineTypes.map(mineType => ({
          value: mineType.name,
          label: mineType.name,
          count: mineType.count
        }));

      case 'mine-status':
        const mineStatuses = data.options || [];
        return mineStatuses.map(status => ({
          value: status.name,
          label: status.name,
          count: status.count
        }));

      default:
        return [];
    }
  }

  /**
   * Legacy method wrappers for backward compatibility
   * These can be removed once all consumers are updated
   */

  async getCountryFilterOptions() {
    return this.getFilterOptions('countries');
  }

  async getCommodityFilterOptions() {
    return this.getFilterOptions('commodities');
  }

  async getMineTypeFilterOptions() {
    return this.getFilterOptions('mine-types');
  }

  async getMineStatusFilterOptions() {
    return this.getFilterOptions('mine-status');
  }
}

// Export singleton instance
export const filterAPI = new FilterAPI();